import {
    Animation,
    ArcRotateCamera,
    FreeCamera,
    Vector3,
} from "@babylonjs/core"
import { CCUtils, prettyJson } from "../../../common/CCUtils"
import { log } from "../../../common/Logging"
import { MazeStore } from "./MazeStore"
import { SimpleCameraInput } from "./SimpleCameraInput"

export enum CameraType {
    ARC_ROTATE,
    FREE,
}

export class Cameras {
    static readonly CAMERA_MOVE_DURATION = 1000

    freeCamera: FreeCamera
    arcRotateCamera: ArcRotateCamera

    rotateStartView = new Vector3(-5, -4, -40)
    cameraStart = new Vector3(this.rotateStartView.x, 6, this.rotateStartView.z)
    mazeCenter = new Vector3(-5, 5, 41)

    cameraUp = false
    currentCamera: CameraType

    constructor() {
        this.setupArcCamera()
        this.setupFreeCamera()
        this.useCamera(CameraType.ARC_ROTATE)
    }

    useCamera = (cameraType: CameraType) => {
        const { scene, gui } = MazeStore.instance
        if (cameraType === CameraType.ARC_ROTATE) {
            scene.activeCamera = this.arcRotateCamera
            this.currentCamera = CameraType.ARC_ROTATE
            if (gui) {
                gui.setShowMoveButtons(false)
            }
        } else {
            scene.activeCamera = this.freeCamera
            this.currentCamera = CameraType.FREE
            if (gui) {
                gui.setShowMoveButtons(true)
            }
        }
    }

    autoRotateCamera = (up?: boolean) => {
        return new Promise<void>((resolve) => {
            if (
                (up === true && this.cameraUp) ||
                (up === false && !this.cameraUp)
            ) {
                resolve()
            } else {
                const done = (doneIntervalId: number) => {
                    cancelAnimationFrame(doneIntervalId)
                    this.cameraUp = !this.cameraUp
                    resolve()
                }

                const rotateAmount = 0.007
                const radiusIncrement = 0.12
                const xIncrement = 16 / 300

                log.debug(`Camera beta on start: ${this.arcRotateCamera.beta}`)

                const move = () => {
                    const intervalId = window.requestAnimationFrame(move)

                    const times = this.cameraUp ? 1 : -1
                    this.arcRotateCamera.beta += rotateAmount * times
                    this.arcRotateCamera.radius -= radiusIncrement * times
                    this.arcRotateCamera.target.x -= xIncrement * times
                    if (
                        this.cameraUp
                            ? this.arcRotateCamera.beta > 1.54
                            : this.arcRotateCamera.beta < 0.06
                    ) {
                        log.debug(
                            `Camera beta on done: ${this.arcRotateCamera.beta}`,
                        )
                        done(intervalId)
                    }
                }
                move()
            }
        })
    }

    addFreeCameraInputs = () => {
        const { canvas } = MazeStore.instance
        const camera = this.freeCamera
        camera.inputs.clear()
        camera.inputs.add(new SimpleCameraInput())

        // Attach the camera to the canvas.
        camera.attachControl(canvas, true)

        const gui = MazeStore.instance.gui
        if (gui) {
            gui.enableMoveButtons()
        }
    }

    removeFreeCameraInputs = () => {
        this.freeCamera.inputs.clear()
        const gui = MazeStore.instance.gui
        if (gui) {
            gui.enableMoveButtons(false)
        }
    }

    targetFreeCameraAtAndMoveThrough = async (
        target: Vector3,
        moveThrough: Array<Vector3>,
    ) => {
        let idx = 0
        for await (const moveTo of moveThrough) {
            log.debug(
                `Moving camera for moveThrough idx ${idx} moving to ${prettyJson(
                    moveTo,
                )} now: ${Date.now()}`,
            )
            await this.targetFreeCameraAtAndMoveTo(
                moveTo,
                idx === moveThrough.length - 1 ? target : undefined,
            )
            idx++
        }
    }

    moveIntoMaze = () => {
        const { cameras, scene, mazeWalls } = MazeStore.instance
        return new Promise<void>((resolve) => {
            const doorVector = mazeWalls.portcullis.position
            const cameraVector = cameras.freeCamera.position
            const moveTo = new Vector3(
                doorVector.x,
                cameraVector.y,
                doorVector.z + 2,
            )

            const frameRate = 26
            const movein = new Animation(
                "movein",
                "position",
                frameRate,
                Animation.ANIMATIONTYPE_VECTOR3,
                Animation.ANIMATIONLOOPMODE_CONSTANT,
            )
            const moveinKeys = []
            moveinKeys.push({
                frame: 0,
                value: cameraVector,
            })
            moveinKeys.push({
                frame: 2 * frameRate,
                value: moveTo,
            })
            movein.setKeys(moveinKeys)

            const movingCamera = scene.beginDirectAnimation(
                cameras.freeCamera,
                [movein],
                0,
                3 * frameRate,
                false,
            )
            movingCamera.onAnimationEnd = resolve
        })
    }

    private targetFreeCameraAtAndMoveTo = (moveTo: Vector3, target?: Vector3) =>
        new Promise<void>((resolve) => {
            const current = this.freeCamera.getTarget()
            const rotateMs = Cameras.CAMERA_MOVE_DURATION
            const interval = 10
            const steps = rotateMs / interval

            let xChange
            let zChange
            if (target) {
                xChange = target.x - current.x
                zChange = target.z - current.z
            }
            const xMoveChange = moveTo.x - this.freeCamera.position.x
            const zMoveChange = moveTo.z - this.freeCamera.position.z
            const cameraTargets: Array<{
                target: Vector3
                moveTo: Vector3
            }> = []
            for (let idx = 1; idx < steps; idx++) {
                cameraTargets.push({
                    target: target
                        ? new Vector3(
                              current.x + (xChange * idx) / steps,
                              current.y,
                              current.z + (zChange * idx) / steps,
                          )
                        : undefined,
                    moveTo: new Vector3(
                        this.freeCamera.position.x +
                            (xMoveChange * idx) / steps,
                        this.freeCamera.position.y,
                        this.freeCamera.position.z +
                            (zMoveChange * idx) / steps,
                    ),
                })
            }
            cameraTargets.push({
                target: target
                    ? new Vector3(target.x, current.y, target.z)
                    : undefined,
                moveTo: new Vector3(
                    moveTo.x,
                    this.freeCamera.position.y,
                    moveTo.z,
                ),
            })
            // log.debug(`Camera current: ${current} target: ${target} targets: ${prettyJson(cameraTargets)}`)
            const intervalId = window.setInterval(() => {
                const targetInfo = cameraTargets.shift()
                if (target) {
                    this.freeCamera.setTarget(targetInfo.target)
                }
                this.freeCamera.position = targetInfo.moveTo
                if (cameraTargets.length === 0) {
                    clearInterval(intervalId)
                    resolve()
                }
            }, interval)
        })

    private setupFreeCamera = () => {
        const { scene } = MazeStore.instance
        const camera = new FreeCamera("camera1", this.cameraStart, scene)
        this.freeCamera = camera

        camera.applyGravity = true
        camera.checkCollisions = true

        camera.setTarget(this.mazeCenter)

        camera.ellipsoid = new Vector3(2, 3, 2)

        camera.keysDown = [87, 40]
        camera.keysUp = [53, 38]
        camera.keysRight = [68, 39]
        camera.keysLeft = [65, 37]

        this.addFreeCameraInputs()
    }

    private setupArcCamera = () => {
        const { scene, canvas } = MazeStore.instance
        const camera = new ArcRotateCamera(
            "camera1",
            0,
            0,
            10,
            this.rotateStartView,
            scene,
        )
        this.arcRotateCamera = camera
        if (!CCUtils.debug) {
            camera.inputs.clear()
        }

        // Target the camera to scene origin.
        camera.setTarget(this.mazeCenter)

        // Attach the camera to the canvas.
        camera.attachControl(canvas, true)
    }
}
